/*
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: Dashboard
 *| (C) Copyright IBM Corp. 2018, 2020
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */
define([
	'underscore',
	'../../lib/@waca/core-client/js/core-client/ui/core/Class',
	'./IRUtils',
	'../../lib/@waca/dashboard-common/dist/core/UniqueHashIdBuilder',
	'../../filters/FilterSpecHelper'
], function (
	_,
	Class,
	IRUtils,
	UniqueHashIdBuilder,
	FilterSpecHelper
) {
	'use strict';

	let _cntr = 0;

	const SLOT_TYPE_MAP = {
		'fact': 'ordinal',
		'attribute': 'category'
	};


	let SmartsCompareCardRecommenderHelper = Class.extend({

		// constructor
		init: function (columnIds, requestOptions) {
			this.columnIds = columnIds;
			this.requestOptions = requestOptions;

			this.id = 'id_' + ++_cntr;
			SmartsCompareCardRecommenderHelper.inherited('init', this, arguments);
		},


		getRequestParameters: function( bPartialTempModule ){

			const { columnIds, requestOptions } = this;
			const { assetId, numResults, sourceType, module } = requestOptions;
			const columns =  this._getColumnsInfo(columnIds);

			const _columnIds = columns
				.map( column => column.id)
				.reduce((acc, columnIds) => acc.concat(columnIds), []);

			const tempModuleJSON = ( bPartialTempModule
				? module.copyPartialMoserModuleJSON(_columnIds)
				: module.getTemporarySessionModuleJSON() );

			const widgetSpec = requestOptions.widgetSpec;
			const visualization = requestOptions.visualization;

			const filters = this._toIRFilters(visualization, widgetSpec);
			const irContent = {
				id: this.id,
				topicId: '',
				combinations: [
					{
						columnCombinations: this._getColumnCombinationsForIR(visualization),
						filters: filters
					}
				]
			};

			const requestParameters = {
				sourceModuleId: assetId,
				sourceModule: JSON.stringify( tempModuleJSON ),
				sourceType: sourceType,
				includedColumns: [],
				excludedColumns: [],
				shapedModule: (!!module.getShapingId()), //module has shaping
				irContents: [irContent],
				maxCardsPerIR: numResults || 10
			};

			return module.saveTemporaryModule().then((shapingModuleId) => {
				if (assetId !== shapingModuleId) {
					requestParameters.tempModuleId = shapingModuleId;
				}
				return requestParameters;
			});
		},

		_restoreHiddenCustomGroupCols: function(irContent, replacements = []) {
			if (replacements.length === 0) {
				return;
			}
			Object.keys(irContent).forEach(key => {
				irContent[key].forEach(irContentItem => {
					(irContentItem.combinations || []).forEach(combination => {
						(combination.columnCombinations || []).forEach((columnCombination, i) => {
							const replacement = replacements.find(item => item.index === i);
							if (replacement && replacement.to === columnCombination.id) {
								columnCombination.id = replacement.from;
							}
						});
					});
				});
			});
		},

		_replaceHiddenCustomGroupCols: function(columns, options) {
			const { visualization, moserDataSources } = options;
			const sourceId = visualization.getDataSource().getId();
			if (moserDataSources && moserDataSources.isConsumerGroupColumn) {
				columns.forEach( (columnEntry, i) => {
					const itemId = columnEntry.itemId;
					if (moserDataSources.isConsumerGroupColumn(sourceId, itemId)) {
						const customGroup = moserDataSources.getCustomGroupData(sourceId, itemId);
						if (customGroup && customGroup.basedOnMoserObjectId) {
							const from = columns[i].itemId;
							const to = customGroup.basedOnMoserObjectId;
							columns[i].itemId = to;
							if (!this.requestOptions.hiddenCustomGroupColumnReplacements) {
								this.requestOptions.hiddenCustomGroupColumnReplacements = [];
							}
							this.requestOptions.hiddenCustomGroupColumnReplacements.push({
								index: i,
								from: from,
								to: to
							});
						}
					}
				} );
			}
		},

		_getColumnCombinationsForIR: function(visualization) {
			const _columnCombinations = this._getColumnCombinations(visualization);
			this._replaceHiddenCustomGroupCols(_columnCombinations, {
				visualization,
				moserDataSources: this.requestOptions.moserDataSources
			});
			const colComboForIR = _columnCombinations.map( (entry) => {
				let irEntry = {
					id: entry.itemId
				};
				if( entry.aggregation ) {
					irEntry.aggregation = entry.aggregation;
				}
				if( entry.autoGrouping ) {
					irEntry.autoGrouping = entry.autoGrouping;
				}
				return irEntry;
			});
			return colComboForIR;
		},

		_getColumnCombinations: function(visualization) {
			let combinations = [];
			const slots = visualization.getSlots().getMappedSlotList();
			slots.forEach(  (slotAPI) => {
				const dataItems = slotAPI.getDataItemList(true /* ignoreDefaultDataItemMapping */);
				dataItems.forEach( (dataItem) => {
					const slotAggType = dataItem.getAggregation();
					const id = dataItem.getId();
					const itemId = dataItem.getColumnId();
					const dataType = dataItem.getMetadataColumn().getType();
					const label = dataItem.getLabel();
					const finalSlotType = SLOT_TYPE_MAP[dataItem.getType()];
					if( itemId !== '_multiMeasuresSeries' ) {
						if( slotAggType &&  finalSlotType === 'ordinal'  ) {
							combinations.push({
								id: id,
								itemId: itemId,
								dataType: dataType,
								finalSlotType: finalSlotType,
								label: label,
								aggregation: IRUtils.IRAggregateType(slotAggType) //TODO this is IR aggregation type, could keep it dashboard and transform to IR where used
							});
						} else {
							let autoGrouping;
							if( dataItem.getBinning()) {
								autoGrouping = {
									size: dataItem.getBinning().bins
								};
							}
							combinations.push({
								id: id,
								itemId: itemId,
								dataType: dataType,
								finalSlotType: finalSlotType,
								label: label,
								autoGrouping: autoGrouping
							});
						}
					}
				});
			});
			return combinations;
		},


		transformCompareRecommendationResponse: function(module, widgetSpec, irContent) {
			//TODO , requestOptions.module, requestOptions.widgetSpec
			const { requestOptions } = this;
			const postSpec = this._fromIRContent(requestOptions, widgetSpec, irContent);
			return postSpec;
		},

		_getColumnsInfo: function( columnIds ){
			return columnIds.map(columnId => ({ id: [columnId] }));
		},

		_toIRFilters: function(visualization, widgetSpec) {
			const filters = this._getFilters(visualization);
			const topBottom = this._getTopBottom(widgetSpec);

			const filtersForRequest = [];

			IRUtils.localFilters2IRFilters(filters, filtersForRequest, visualization);
			IRUtils.topBottomToIRFilters(topBottom, filtersForRequest);

			return filtersForRequest;
		},

		//TODO: this file is getting big, breakup/move utility functions


		_getFilters: function(visualization) {
			//NOT ideal, have to use local filter spec
			let localFilters = visualization.getLocalFilters().getFilterList();
			if( !localFilters) {
				localFilters = [];
			}
			const leafFilters = [];

			// We need to convert all the filters to leaf level filters (ones with columnId),
			// beacuse some cases, the localfilter is a datapoint filter, which is a compound filter (no columnId)
			localFilters.forEach(localFilter => this._collectLeafFilterEntries(localFilter, leafFilters));
			return JSON.parse( JSON.stringify(leafFilters) );
		},

		/**
		 * Given a filter, collect its leaf level filters
		 * @param {Object} filter the filter spec
		 * @param {Array} leafFilterEntries an array used to collect the leaf level filters (ones with columnId)
		 */
		_collectLeafFilterEntries: function(filter, leafFilterEntries) {
			if (FilterSpecHelper.isCompound(filter)) {
				filter.values.forEach((value) => {
					if (value) {
						this._collectLeafFilterEntries(value, leafFilterEntries);
					}
				});
			} else {
				leafFilterEntries.push(filter);
			}
		},

		_getTopBottom: function(orgWidgetSpec) {
			//NOT ideal, have to read spec
			const topBottom = [];

			const widgetSpec = JSON.parse(JSON.stringify(orgWidgetSpec));

			const  dataViews = widgetSpec.data.dataViews || [];
			dataViews.forEach(viewItem => {
				const dataItems = viewItem.dataItems|| [];
				dataItems.forEach(item => {

					const selectionItems = item.selection || [];

					selectionItems.forEach(selection => {
						if(selection && selection.topBottom) {
							topBottom.push({
								id: item.id,
								itemId: item.itemId,
								selection: selection
							});
						}
					});

				});
			});

			return topBottom;
		},

		/*
		sample IR response
		{
			  "smartsStatus" : "OK",
			  "ircontent" : {
			    "id" : {
			      "id" : "id",
			      "topicId" : "Movies.Budget",
			      "combinations" : [ {
			        "columnCombinations" : [ "Movies.Genre", "Movies.Income", "Movies.Gross" ],
			        "filters" : [ {
			          "id" : "Movies.Budget",
			          "filterId" : "0",
			          "lowValue" : "5",
			          "highValue" : "10",
			          "invertFlag" : false,
			          "type" : "NUMERIC",
			          "numericOperator" : "LESS_THAN"
			        }, {
			          "id" : "Movies.Genre",
			          "filterId" : "0",
			          "topBottomType" : "TOP",
			          "method" : "PERCENTAGE",
			          "byColumn" : "Movies.Budget",
			          "domainSize" : 10,
			          "type" : "TOP_BOTTOM"
			        }, {
			          "id" : "Movies.Budget",
			          "filterId" : "0",
			          "label" : "Include",
			          "values" : [ {
			            "uniqueValue" : "Movies.Studio.Columbia",
			            "displayValue" : "Columbia"
			          } ],
			          "type" : "INCLUSION_EXCLUSION",
			          "include" : true
			        } ],
			        "score" : 0.0
			      } ],
			      "score" : 0.0
			    }
			  }
			}
		*/

		_fromIRContent: function(requestOptions, widgetSpec, irContent) {
			const { visualization } = requestOptions;
			const candidateWidgetSpecs = [];

			const currentColumnCombinations = this._getColumnCombinations(visualization);
			this._restoreHiddenCustomGroupCols(irContent, requestOptions.hiddenCustomGroupColumnReplacements);
			if(irContent && irContent[this.id]) {
				irContent[this.id].forEach(irContentItem => {
					const widgetSpecs = this._processIRContent(irContentItem, widgetSpec, currentColumnCombinations, irContentItem.details, visualization);
					candidateWidgetSpecs.push(...widgetSpecs);
				});
			}

			return candidateWidgetSpecs;
		},

		_processIRContent: function(irContent, widgetSpec, currentColumnCombinations, reason, visualization) {
			const { combinations } = irContent;

			const candidateWidgetSpecs = [];


			const details = {
				reason
			};

			combinations.forEach(irCandidate => {

				const filters = {
					local: [],
					topBottom: []
				};

				const irFilters = irCandidate.filters || [];


				irFilters.forEach(irFilter => {
					if(irFilter.type === 'TOP_BOTTOM') {
						const filter = IRUtils.IRFilters2TopBottom(irFilter, widgetSpec, currentColumnCombinations, details, visualization);
						if (filter) {
							filters.topBottom.push(filter);
						}
					} else {
						const filter = IRUtils.IRFilters2LocalFilter(irFilter, widgetSpec, currentColumnCombinations, details, visualization);
						if (filter) {
							filters.local.push(filter);
						}
					}
				});

				widgetSpec.titleHtml = undefined;
				widgetSpec.name = undefined;

				let dataItemIdsMap = {};
				const candidateWidgetSpec = this._transform2WidgetSpec(widgetSpec, currentColumnCombinations, irCandidate.columnCombinations, filters, details, visualization, dataItemIdsMap);
				if(candidateWidgetSpec) {
					candidateWidgetSpecs.push({
						spec: candidateWidgetSpec,
						details,
						score: irCandidate.score
					});
				}
			});

			return candidateWidgetSpecs;
		},

		_transform2WidgetSpec: function(widgetSpec, currentColumnCombinations, columnCombinations, filters, details, visualization, dataItemIdsMap) {
			let spec = JSON.parse(JSON.stringify(widgetSpec));

			this._assimilateTopBottom(spec, filters.topBottom, currentColumnCombinations,dataItemIdsMap);
			this._assimilateFilters(spec, filters.local);
			this._assimilateComparableMeasures(spec, columnCombinations, currentColumnCombinations, details, visualization,dataItemIdsMap);

			return spec;
		},

		_replaceSlotId: function(spec, oldId, newId) {
			spec.slotmapping.slots.forEach((item, index) => {
				const dataItemIndex = item.dataItems.indexOf(oldId);
				if (dataItemIndex !== -1) {
					spec.slotmapping.slots[index].dataItems[dataItemIndex] = newId;
				}
			});
		},

		_assimilateTopBottom: function(spec, topBottomItems, currentColumnCombinations, dataItemIdsMap) {
			topBottomItems.forEach((item) => {
				this._assimilateTopBottomItem(spec, item, currentColumnCombinations,dataItemIdsMap);
			});
		},

		_findMatchingColumnEntry:function(topBottomSpec, currentColumnCombinations) {
			let currentColEntry;
			currentColumnCombinations.some( ( entry ) => {
				if(entry.id === topBottomSpec.id) {
					currentColEntry = entry;
					return true;
				}
			});

			if( !currentColEntry ) {
				const isAppropriateSlotForTopBottom = (entry, topBottomSpec) => {
					if(topBottomSpec.selection[0].context) {
						//is contextual, should apply on a category
						if(entry.finalSlotType !== 'ordinal') {
							return true;
						} else {
							return false;
						}
					}
					return true;
				};
				if( currentColumnCombinations.length === 1 ) {
					currentColEntry = (currentColumnCombinations[0].itemId === topBottomSpec.itemId) ? currentColumnCombinations[0] : undefined;
				} else {
					currentColumnCombinations.some( ( entry ) => {
						if((entry.itemId === topBottomSpec.itemId) && isAppropriateSlotForTopBottom(entry,topBottomSpec)) {
							currentColEntry = entry;
							return true;
						}
					});
				}

			}
			return currentColEntry;
		},

		_assimilateTopBottomItem: function(spec, topBottomSpec, currentColumnCombinations, dataItemIdsMap) {

			let currentColEntry = this._findMatchingColumnEntry( topBottomSpec, currentColumnCombinations );
			if(!currentColEntry) {
				return;
			}

			let dataItemRef = IRUtils.findDataItem(spec, (dataItem) => {
				return dataItem.id === currentColEntry.id;
			});


			if (!dataItemRef) {
				// unable to find proper data item
				return;
			}

			const originalDataItem = spec.data.dataViews[dataItemRef.viewIndex].dataItems[dataItemRef.viewItemIndex];

			const oldId = originalDataItem.id;
			const newId = UniqueHashIdBuilder.createUniqueHashId(originalDataItem.itemId, dataItemIdsMap);
			dataItemIdsMap[newId] = newId;

			spec.data.dataViews[dataItemRef.viewIndex].dataItems[dataItemRef.viewItemIndex] = {
				...topBottomSpec,
				id: newId
			};

			this._replaceSlotId(spec, oldId, newId);
		},

		_assimilateFilters: function(spec, localFilters) {

			if (localFilters.length === 0 && spec.localFilters === undefined) {
				return;
			}

			spec.localFilters = localFilters;
		},

		_assimilateComparableMeasures: function(spec, columnCombinations, currentColumnCombinations, details, visualization, dataItemIdsMap) {

			const _columnCombinations = columnCombinations.map( col =>  col.id );

			const combo = currentColumnCombinations.map(item => item.itemId);
			let [ from ] = _.difference(combo, _columnCombinations);
			const [ to ] = _.difference(_columnCombinations, combo);

			if( !from && !!to) {
				from = combo[0]; //all columns in currentColumnCombinations are the same and is included in the IR _columnCombinations
			}

			let id;
			currentColumnCombinations.forEach( (curr) => {
				if(curr.itemId === from  && curr.finalSlotType != 'category' && !id) {
					id = curr.id;
				}
			} );
			if(!id) {
				currentColumnCombinations.forEach( (curr) => {
					if(curr.itemId === from  && !id) {
						id = curr.id;
					}
				} );
			}

			if (details.reason === 'REPLACE_COMPARABLE_MEASURE') {

				details.type = 'REPLACE_COMPARABLE_MEASURE';

				details.from = IRUtils.getColumnInfo(currentColumnCombinations, visualization, from);
				details.to = IRUtils.getColumnInfo(currentColumnCombinations, visualization, to);
			}


			this._assimilateComparableMeasure(spec, from, to, id, dataItemIdsMap);
		},

		_assimilateComparableMeasure: function(spec, from, to, id, dataItemIdsMap) {

			// - update data items with the new top bottom spec + replace old id with new
			const dataItemRef = IRUtils.findDataItem(spec, (dataItem) => {
				return dataItem.itemId === from && dataItem.id === id;
			});

			if (!dataItemRef) {
				return;
			}

			const originalDataItem = spec.data.dataViews[dataItemRef.viewIndex].dataItems[dataItemRef.viewItemIndex];

			const oldId = originalDataItem.id;
			const newId = UniqueHashIdBuilder.createUniqueHashId(originalDataItem.itemId, dataItemIdsMap);
			dataItemIdsMap[newId] = newId;

			const toParts = to.split('.');
			const itemLabel = toParts[toParts.length - 1];

			spec.data.dataViews[dataItemRef.viewIndex].dataItems[dataItemRef.viewItemIndex] = {
				...originalDataItem,
				itemId: to,
				itemLabel,
				id: newId
			};

			this._replaceSlotId(spec, oldId, newId);
		}
	});
	return SmartsCompareCardRecommenderHelper;
});
